import { useId } from "react";
import {
  READER_CONFIDENCE_ANCHORS,
  READER_CONFIDENCE_LABEL,
  parseReaderConfidence,
} from "@/utils/readerConfidence";

/**
 * Required integer 1–100 rating (ENG-262). The raw text is kept as typed, so
 * invalid input stays visible and blocks Save/Accept instead of being coerced.
 * Enter submits when the value is valid.
 */
export function ReaderConfidenceInput({
  value,
  onChange,
  onSubmit,
  disabled = false,
  compact = false,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  disabled?: boolean;
  /** Card layout: short label, smaller field. */
  compact?: boolean;
}) {
  const hintId = useId();
  const valid = parseReaderConfidence(value) != null;
  const invalidText = value.trim() !== "" && !valid;
  return (
    <div
      className="flex flex-col gap-0.5 font-mono text-[10px] text-fg-muted"
      onClick={(event) => event.stopPropagation()}
    >
      <label className="flex items-center gap-1.5">
        <span className={compact ? "uppercase tracking-wide" : "whitespace-nowrap"}>
          {compact ? "Confidence" : READER_CONFIDENCE_LABEL}
        </span>
        <input
          type="text"
          inputMode="numeric"
          aria-label={READER_CONFIDENCE_LABEL}
          aria-describedby={hintId}
          aria-invalid={!valid || undefined}
          placeholder="1–100"
          title={READER_CONFIDENCE_ANCHORS}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter" && valid && onSubmit) {
              event.preventDefault();
              onSubmit();
            }
          }}
          className={`${compact ? "h-6 w-12" : "h-7 w-14"} rounded-[2px] border bg-bg-2 px-1.5 text-center text-[11px] text-fg tabular-nums outline-none focus:border-signal disabled:opacity-40 ${
            valid ? "border-border-strong" : "border-warn/60"
          }`}
        />
      </label>
      <span id={hintId} className={`text-[9px] ${invalidText ? "text-warn" : ""}`}>
        {invalidText ? "Whole number 1–100 only" : READER_CONFIDENCE_ANCHORS}
      </span>
    </div>
  );
}
